/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Button, Grid, Icon, PopOut, Spinner } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { P, TextBlock } from "@cd2/text/"
import { useTheme } from "@cd2/theme"
import { useEffect, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import {
  combineProductLocationStockData,
  StockDisplay,
  useProductLocationStock,
} from "../apiHooks/productLocations"
import { icons } from "../constants/icons"
import { zIndexes } from "../constants/zIndexes"
import { useOtherBranchStockModal } from "../hooks/useOtherBranchStockModal"
import { IRecord } from "../types/schema"
import { brandColors, themeColors } from "./EOSThemeProvider"
import { ExternalLink } from "./ExternalLink"
import { useGlobalState } from "./GlobalStateProvider"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { useStateDebounced } from "../hooks/useDebouncedState"
import { useApi } from "../hooks/useApi"
import { useSettings } from "../utils/useSettings"
import { rankProductSearchResults } from "../utils/productSearchRanking"
import { HighlightedText } from "./HighlightedText"

type IProduct = IRecord<
  "products",
  "stock_code" | "legacy_stock_code" | "body" | "line_item_description" | "active"
> & {
  product_locations: (IRecord<"product_locations", "name" | "specific_location"> & {
    warehouse?: IRecord<"warehouses", "name">
  })[]
}

export const STOCKLOOKUP_DEBOUNCE_TIME = 100

/** how many results the list shows; one more match than this shows "View all" */
const STOCKLOOKUP_VISIBLE_RESULTS = 10

export function StockLookup() {
  const { currentUser } = useGlobalState()
  const api = useApi()
  const { stockWarehouses, warehouses } = useSettings()
  const history = useHistory()
  const [query, debouncedQuery, setQuery] = useStateDebounced("", STOCKLOOKUP_DEBOUNCE_TIME)
  const [focused, setFocused] = useState(false)
  const [shouldUnfocus, setShouldUnfocus] = useState(false)

  const [autoFocus, setAutoFocus] = useState(false)
  const [inputKey, setInputKey] = useState(1)

  const branchId = currentUser.branch.id!
  const warehouseNames =
    stockWarehouses && stockWarehouses.length > 0
      ? stockWarehouses.map(w => w.name)
      : warehouses.map(w => w.name)

  const { data, isFetching, error } = useQuery({
    // everything the search depends on, or results fetched for another branch or warehouse
    // setup would be reused for the 30 seconds they stay fresh
    queryKey: ["productStockLookup", debouncedQuery, branchId, warehouseNames],
    // must search on debouncedQuery (the key), not the live query, or results can be cached
    // under a key they don't belong to
    queryFn: async () => {
      if (!debouncedQuery.trim()) {
        return null
      }

      // stock is only fetched so in stock products rank first, as in the product select lists
      const search = await api.searchProductsForSelect(
        {
          query: debouncedQuery,
          branchId,
          warehouseNames,
          include: { stock: true, locations: true },
        },
        { retries: 3 },
      )
      if (!search.success) {
        throw new Error(`Stock lookup failed: ${JSON.stringify(search.error)}`)
      }

      const ranked = rankProductSearchResults(search.data, debouncedQuery)
      return {
        // the query these results are for, to highlight against while newer results load
        query: debouncedQuery,
        hasMore: ranked.length > STOCKLOOKUP_VISIBLE_RESULTS,
        products: ranked
          .slice(0, STOCKLOOKUP_VISIBLE_RESULTS)
          .map(
            product =>
              ({ ...product, product_locations: product.product_locations ?? [] } as IProduct),
          ),
      }
    },
    staleTime: 30_000,
    // keep showing the last results while the next search loads, rather than the list blinking out
    placeholderData: keepPreviousData,
  })

  const listRef = useRef<HTMLDivElement>(null)
  /** the last click inside the list's React tree, including portaled children such as the
   * "All stock" modal, which aren't DOM descendants of listRef. Stored as the event rather than
   * a flag so a click stopped before reaching the document can't leak into the next one */
  const lastListClickRef = useRef<Event | null>(null)

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick, false)
    document.addEventListener("keydown", handleDocumentKeydown, false)

    return () => {
      document.removeEventListener("click", handleDocumentClick)
      document.removeEventListener("keydown", handleDocumentKeydown)
    }
  })

  /** this is really hacky but i couldn't get .focus)_ on an inputref to work so on ctrl + 1 keydown, it sets autofocus to true
   * and then we rebuild component from scratch with a key change so autofocus is true on mount..
   */
  useEffect(() => {
    if (autoFocus) {
      setInputKey(k => k + 1)
    }
  }, [autoFocus])

  /** if the input was blurred and we didn't click on the list, blank everything.
   * we do this so they can interact with the list after input focus has been lost
   */
  function handleDocumentClick(e: MouseEvent) {
    const clickedInsideList =
      lastListClickRef.current === e ||
      (!!listRef.current && e.composedPath().includes(listRef.current))

    if (shouldUnfocus && !clickedInsideList) {
      handleReset()
    }
  }

  function handleReset() {
    setFocused(false)
    setShouldUnfocus(false)
    setQuery("")
    setAutoFocus(false)
  }

  function handleDocumentKeydown(e: KeyboardEvent) {
    if (e.ctrlKey && e.key === "1") {
      setAutoFocus(true)
    }
  }

  if (error) {
    console.error(error)
  }

  const shouldShowList = focused && (data?.products.length ?? 0) > 0

  return (
    <div
      css={css`
        text-align: left;
        position: relative;
        z-index: 3;
      `}
    >
      <Fields.Text
        key={inputKey}
        autoFocus={autoFocus}
        value={query}
        onChange={setQuery}
        placeholder="Search for stock info"
        prefix={<Icon icon={icons.search} />}
        onFocus={() => {
          setShouldUnfocus(false)
          setFocused(true)
        }}
        onBlur={() => setShouldUnfocus(true)}
        inputProps={{
          autoComplete: "off",
        }}
      />
      <div
        ref={listRef}
        onClickCapture={e => {
          lastListClickRef.current = e.nativeEvent
        }}
        css={css`
          position: absolute;
          left: 0px;
          /* wider than the input so descriptions and the buttons have room */
          width: max(100%, 460px);
          max-width: calc(100vw - 32px);
          top: calc(100% - 5px);
          z-index: 100;
          background-color: #fff;
          padding: 4px 0;
          border: 1px solid;
          border-top: 0;
          border-bottom-right-radius: 6px;
          border-bottom-left-radius: 6px;
          transition: 0.1s;
          box-shadow: 0 2px 0 rgba(0, 0, 0, 0.045);
          border-color: ${focused && !shouldUnfocus ? "#0984e3" : "#DADADA"};
          opacity: ${shouldShowList ? 1 : 0};
          pointer-events: ${shouldShowList ? "initial" : "none"};
        `}
      >
        <TextBlock size="small" blockSpacing={0}>
          <div>
            {(data?.products ?? []).map(product => (
              <StockRow key={product.id} product={product} query={data?.query ?? ""} />
            ))}
            {data?.hasMore && (
              <div
                onClick={() => {
                  handleReset()
                  history.push(`/stock/products?query=${query}`)
                }}
                css={css`
                  text-align: center;
                  padding: 4px;
                  cursor: pointer;
                  font-weight: 600;
                  color: ${themeColors.blue};

                  &:hover {
                    background: ${themeColors.lightestBlue};
                  }
                `}
              >
                View all results
              </div>
            )}
          </div>
        </TextBlock>
      </div>
      {isFetching && (
        <div
          css={css`
            position: absolute;
            right: 8px;
            top: 10px;
            z-index: ${zIndexes.STOCK_LOOKUP_SEARCHING};
          `}
        >
          <Spinner color="text" />
        </div>
      )}
    </div>
  )
}

function StockRow({ product, query }: { product: IProduct; query: string }) {
  const { currentUser } = useGlobalState()
  const allBranchStockModal = useOtherBranchStockModal(product.id)
  const [displayStock, setDisplayStock] = useState(false)

  const description = product.line_item_description || product.body
  const hasLegacyCode =
    !!product.legacy_stock_code &&
    product.legacy_stock_code.toUpperCase() !== product.stock_code.toUpperCase()

  return (
    <div
      css={css`
        padding: 4px 8px;
        background: ${product.active ? "white" : themeColors.lightOrange};
        border-bottom: 1px solid ${themeColors.lightGrey};

        &:last-of-type {
          border-bottom: 0;
        }

        &:hover {
          background: ${product.active ? themeColors.lightestBlue : "#ffcb8a"};
        }
      `}
    >
      {allBranchStockModal.node}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
        `}
      >
        <div
          css={css`
            min-width: 0;
            line-height: 1.3;
            ${product.active ? "" : "font-style: italic;"}
          `}
        >
          <b>
            <HighlightedText text={product.stock_code} query={query} />
            {!product.active && " (Inactive)"}
          </b>
          : <HighlightedText text={description} query={query} />
          <div
            css={css`
              font-size: 12px;
              color: ${themeColors.grey};
            `}
          >
            {hasLegacyCode && (
              <span>
                Previously <HighlightedText text={product.legacy_stock_code} query={query} />
                {" · "}
              </span>
            )}
            {!displayStock && (
              <a
                css={css`
                  cursor: pointer;
                `}
                onClick={() => setDisplayStock(true)}
              >
                Show stock details
              </a>
            )}
          </div>
        </div>
        {currentUser.userAccountType === "branch" && (
          <div
            css={css`
              display: flex;
              gap: 4px;
              flex-shrink: 0;
            `}
          >
            <ExternalLink to={`/stock/products/${product.id}`}>
              <Button extendTheme={{ padding: 4 }}>View</Button>
            </ExternalLink>
            <Button
              extendTheme={{ padding: 4 }}
              buttonColor="white"
              onClick={allBranchStockModal.toggleModal}
            >
              All stock
            </Button>
          </div>
        )}
      </div>
      {displayStock && <InlineStockDetails product={product} />}
    </div>
  )
}

function InlineStockDetails({ product }: { product: IProduct }) {
  const { colors } = useTheme()
  const stockData = useProductLocationStock(product.product_locations.map(pl => pl.id))

  if (!stockData) {
    return <div>Loading stock...</div>
  }
  const stockByWarehouse: any[] = []

  for (const location of product.product_locations) {
    const locationStock = stockData[location.id]
    if (!locationStock) {
      console.warn("no stock data for location", location.id)
      continue
    }
    if (location.warehouse) {
      stockByWarehouse.push({
        warehouse: location.warehouse,
        stock: locationStock,
      })
    }
  }

  const combined = combineProductLocationStockData(stockData)

  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px;
      `}
    >
      <StockDisplay stockValues={combined} unStyled inline>
        {stockByWarehouse.length > 0 && (
          <PopOut
            target={() => (
              <div
                css={css`
                  border-bottom: 1px ${colors.primary} dotted;
                  margin-right: 0.5em;
                `}
              >
                Warehouses: <b>{stockByWarehouse.length}</b>
                {product.product_locations.length > 0 ? ", " : ""}
              </div>
            )}
            render={() => (
              <div
                color="blue"
                css={css`
                  padding: 4px;
                  background: ${colors.blue};

                  p {
                    white-space: nowrap;
                  }
                `}
              >
                <Grid gap={4}>
                  {stockByWarehouse.map(({ warehouse, stock }) => (
                    <div key={warehouse.id}>
                      <P blockSpacing={0.1} color="white" weight={600}>
                        {warehouse.name}
                      </P>
                      <P blockSpacing={0.1} color="white">
                        Allocated stock: <b>{stock.allocatedStock}</b>
                      </P>
                      <P blockSpacing={0.1} color="white">
                        In stock: <b>{stock.inStock}</b>
                        {stock.provisionalStock > 0 && (
                          <span
                            title="Booked in on a goods receipt and allocated, but not yet confirmed by a Sage import"
                            css={css`
                              color: ${brandColors.orange};
                              font-weight: 600;
                              cursor: help;
                            `}
                          >
                            {" "}
                            ({stock.provisionalStock} provisional)
                          </span>
                        )}
                      </P>
                      <P blockSpacing={0.1} color="white">
                        On order: <b>{stock.onSalesOrderStock}</b>
                      </P>
                    </div>
                  ))}
                </Grid>
              </div>
            )}
            placement={"top"}
            openOn={"hover"}
            offset={0}
          />
        )}
        {product.product_locations.length > 0 && (
          <span>
            Locations:{" "}
            <b>
              {product.product_locations
                ?.map(l => l.specific_location)
                .filter(Boolean)
                .join(", ")}
            </b>
          </span>
        )}
      </StockDisplay>
    </div>
  )
}
